import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    UserPlus,
    Loader2,
    ArrowLeft,
    CheckCircle2,
    XCircle,
    Phone,
    Mail,
    Clock,
    Award,
    Sparkles,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";

const STATUSES = [
    { key: "submitted", label: "In Prüfung", tone: "bg-amber-100 text-amber-800 border-amber-300" },
    { key: "contacted", label: "Kontakt aufgenommen", tone: "bg-blue-100 text-blue-800 border-blue-300" },
    { key: "converted", label: "Vermittelt (+50 Pkt)", tone: "bg-green-100 text-green-800 border-green-300" },
    { key: "rejected", label: "Abgelehnt", tone: "bg-red-100 text-red-800 border-red-300" },
];

/**
 * Admin → Vermittler-Empfehlungs-Leads.
 * Workflow: submitted → contacted → converted (+50 Punkte) | rejected.
 * Idempotent: a second 'converted' PATCH does NOT double-credit points.
 */
export default function AdminVermittlerReferralsPage() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [statusFilter, setStatusFilter] = useState("submitted");
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!authLoading && user?.role !== "admin") navigate("/");
    }, [user, authLoading, navigate]);

    const load = async () => {
        setLoading(true);
        try {
            const params = statusFilter ? `?status=${statusFilter}` : "";
            const res = await api.get(`/admin/vermittler/referrals${params}`);
            setItems(res.data.items || []);
            setCounts(res.data.counts || {});
        } catch (e) {
            toast.error(e.response?.data?.detail || "Empfehlungen konnten nicht geladen werden");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.role === "admin") load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, statusFilter]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter((r) => {
            const hay = [
                r.first_name, r.last_name, r.email, r.phone, r.note,
                r.vermittler?.name, r.vermittler?.email, r.vermittler?.short_code,
            ].filter(Boolean).join(" ").toLowerCase();
            return hay.includes(q);
        });
    }, [items, search]);

    if (authLoading || !user || user.role !== "admin") {
        return (
            <div className="flex items-center justify-center min-h-[40vh]">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto px-4 py-8 space-y-6" data-testid="admin-vermittler-referrals-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <Button
                        variant="outline"
                        onClick={() => navigate("/admin")}
                        className="rounded-sm h-9"
                        data-testid="back-to-admin-btn"
                    >
                        <ArrowLeft className="w-4 h-4 mr-1.5" /> Admin
                    </Button>
                    <div>
                        <div className="flex items-center gap-2">
                            <UserPlus className="w-6 h-6 text-[#FFCC00]" />
                            <h1 className="font-display text-2xl font-bold">Vermittler-Empfehlungen</h1>
                        </div>
                        <p className="text-sm text-gray-600 mt-0.5">
                            Manuell erfasste Empfehlungen prüfen, kontaktieren und bei Verkauf <strong>50 Punkte</strong> gutschreiben.
                        </p>
                    </div>
                </div>
            </div>

            {/* Status-Filter mit Counts */}
            <div className="flex flex-wrap gap-2" data-testid="status-filter-pills">
                <FilterPill
                    label="Alle"
                    count={Object.values(counts).reduce((s, n) => s + n, 0)}
                    active={statusFilter === ""}
                    onClick={() => setStatusFilter("")}
                    testid="filter-all"
                />
                {STATUSES.map((s) => (
                    <FilterPill
                        key={s.key}
                        label={s.label}
                        count={counts[s.key] || 0}
                        active={statusFilter === s.key}
                        onClick={() => setStatusFilter(s.key)}
                        testid={`filter-${s.key}`}
                    />
                ))}
            </div>

            {/* Suche */}
            <div className="max-w-md">
                <Input
                    placeholder="Suche nach Name, E-Mail, Telefon, Vermittler oder Code…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="rounded-sm"
                    data-testid="search-input"
                />
            </div>

            {/* Liste */}
            {loading ? (
                <div className="p-12 text-center text-gray-400">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                </div>
            ) : filtered.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-sm p-12 text-center text-gray-500 text-sm" data-testid="referrals-empty">
                    <Sparkles className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    Keine Empfehlungen für diesen Filter.
                </div>
            ) : (
                <div className="space-y-3" data-testid="referrals-list">
                    {filtered.map((r) => (
                        <ReferralRow key={r.id} referral={r} onUpdated={load} />
                    ))}
                </div>
            )}
        </div>
    );
}


function FilterPill({ label, count, active, onClick, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border text-xs font-bold uppercase tracking-wider transition-colors ${
                active
                    ? "bg-[#111827] text-white border-[#111827]"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            }`}
        >
            {label}
            <span className={`px-1.5 py-0.5 rounded-sm font-mono-num text-[10px] ${active ? "bg-white/15" : "bg-gray-100 text-gray-700"}`}>
                {count}
            </span>
        </button>
    );
}


function ReferralRow({ referral, onUpdated }) {
    const [noteOpen, setNoteOpen] = useState(false);
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(null);
    const statusMeta = STATUSES.find((s) => s.key === referral.status) || STATUSES[0];

    const act = async (newStatus, confirmMsg) => {
        if (confirmMsg && !window.confirm(confirmMsg)) return;
        setSubmitting(newStatus);
        try {
            const res = await api.patch(`/admin/vermittler/referrals/${referral.id}`, {
                status: newStatus,
                note: note || undefined,
            });
            if (res.data?.points_awarded) {
                toast.success("Empfehlung als vermittelt markiert — 50 Punkte gutgeschrieben.");
            } else if (newStatus === "converted") {
                toast.success("Status auf 'Vermittelt' aktualisiert (Punkte bereits zuvor vergeben).");
            } else {
                toast.success("Status aktualisiert");
            }
            setNote("");
            setNoteOpen(false);
            onUpdated();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Aktualisierung fehlgeschlagen");
        } finally {
            setSubmitting(null);
        }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-5" data-testid={`referral-row-${referral.id}`}>
            <div className="flex flex-wrap items-start gap-4">
                <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-display font-bold text-base" data-testid="ref-customer-name">
                            {[referral.first_name, referral.last_name].filter(Boolean).join(" ") || "—"}
                        </div>
                        <Badge className={`text-[10px] font-bold uppercase tracking-wider border ${statusMeta.tone} hover:${statusMeta.tone}`}>
                            {statusMeta.label}
                        </Badge>
                        {referral.points_awarded_at && (
                            <Badge className="bg-green-100 text-green-800 hover:bg-green-100 text-[10px] font-bold uppercase tracking-wider border-green-300">
                                <Award className="w-3 h-3 mr-1" /> +{referral.points_awarded || 50} Pkt vergeben
                            </Badge>
                        )}
                        {/* Phase 4.12 (M5) — Lead-Hotness-Score */}
                        {referral._hotness && (
                            <Badge
                                className={`text-[10px] font-bold uppercase tracking-wider border ${
                                    referral._hotness.level === "hot" ? "bg-red-100 text-red-800 border-red-300" :
                                    referral._hotness.level === "warm" ? "bg-amber-100 text-amber-800 border-amber-300" :
                                    "bg-gray-100 text-gray-700 border-gray-300"
                                } hover:bg-opacity-100`}
                                data-testid={`ref-hotness-${referral.id}`}
                                title={referral._hotness.reasons?.map((r) => `+${r.points} ${r.label}`).join("\n")}
                            >
                                🔥 {referral._hotness.level} · {referral._hotness.hotness}
                            </Badge>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-700">
                        {referral.email && (
                            <a href={`mailto:${referral.email}`} className="inline-flex items-center gap-1 hover:text-[#111827] underline-offset-2 hover:underline" data-testid="ref-email">
                                <Mail className="w-3 h-3" /> {referral.email}
                            </a>
                        )}
                        {referral.phone && (
                            <a href={`tel:${referral.phone}`} className="inline-flex items-center gap-1 hover:text-[#111827] underline-offset-2 hover:underline" data-testid="ref-phone">
                                <Phone className="w-3 h-3" /> {referral.phone}
                            </a>
                        )}
                        {referral.city && (
                            <span className="inline-flex items-center gap-1 text-gray-600" data-testid="ref-city">
                                📍 {referral.city}
                            </span>
                        )}
                        <span className="inline-flex items-center gap-1 text-gray-500 font-mono-num">
                            <Clock className="w-3 h-3" /> {new Date(referral.created_at).toLocaleString("de-DE")}
                        </span>
                    </div>
                    {referral.note && (
                        <p className="text-xs text-gray-600 mt-2 italic border-l-2 border-gray-200 pl-3">
                            „{referral.note}"
                        </p>
                    )}
                    <div className="mt-3 pt-2 border-t border-gray-100 text-[11px] text-gray-500 flex items-center gap-2 flex-wrap">
                        <span className="font-bold uppercase tracking-wider">Vermittler:</span>
                        <span>{referral.vermittler?.name || "—"}</span>
                        {referral.vermittler?.email && <span className="text-gray-400">· {referral.vermittler.email}</span>}
                        {referral.vermittler?.short_code && (
                            <span className="font-mono-num bg-gray-100 px-1.5 py-0.5 rounded-sm text-gray-700">
                                {referral.vermittler.short_code}
                            </span>
                        )}
                    </div>
                    {referral.admin_note && (
                        <div className="mt-2 text-[11px] text-gray-500">
                            <span className="font-bold uppercase tracking-wider">Admin-Notiz:</span> {referral.admin_note}
                        </div>
                    )}
                </div>

                <div className="flex flex-col gap-2 shrink-0 min-w-[200px]">
                    <Button
                        size="sm"
                        onClick={() => act("contacted")}
                        disabled={!!submitting || referral.status === "contacted"}
                        variant="outline"
                        className="rounded-sm h-9 text-xs font-bold uppercase tracking-wider"
                        data-testid="ref-action-contacted"
                    >
                        {submitting === "contacted" ? <Loader2 className="w-3 h-3 animate-spin" /> : "Kontaktiert"}
                    </Button>
                    <Button
                        size="sm"
                        onClick={() => act("converted", referral.points_awarded_at
                            ? "Diese Empfehlung wurde bereits vermittelt — Status erneut setzen?"
                            : "Empfehlung als 'Vermittelt' markieren und dem Vermittler 50 Punkte gutschreiben?")}
                        disabled={!!submitting}
                        className="rounded-sm h-9 text-xs font-bold uppercase tracking-wider bg-[#16A34A] hover:bg-[#15803D] text-white"
                        data-testid="ref-action-converted"
                    >
                        {submitting === "converted"
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Vermittelt +50 Pkt</>}
                    </Button>
                    <Button
                        size="sm"
                        onClick={() => act("rejected", "Empfehlung wirklich ablehnen?")}
                        disabled={!!submitting || referral.status === "rejected"}
                        variant="outline"
                        className="rounded-sm h-9 text-xs font-bold uppercase tracking-wider border-red-300 text-red-700 hover:bg-red-50"
                        data-testid="ref-action-rejected"
                    >
                        {submitting === "rejected" ? <Loader2 className="w-3 h-3 animate-spin" /> : <><XCircle className="w-3.5 h-3.5 mr-1.5" /> Ablehnen</>}
                    </Button>
                    <button
                        type="button"
                        onClick={() => setNoteOpen((v) => !v)}
                        className="text-[10px] uppercase tracking-wider text-gray-500 hover:text-[#111827] font-bold mt-1"
                        data-testid="ref-toggle-note"
                    >
                        {noteOpen ? "Notiz schließen" : "Admin-Notiz hinzufügen"}
                    </button>
                </div>
            </div>
            {noteOpen && (
                <Textarea
                    placeholder="Optionale Notiz, die mit dem nächsten Status-Update gespeichert wird (max 500 Zeichen)"
                    value={note}
                    onChange={(e) => setNote(e.target.value.slice(0, 500))}
                    rows={2}
                    className="rounded-sm text-sm mt-3"
                    data-testid="ref-note-input"
                />
            )}
        </div>
    );
}
